import React from 'react';
import classNames from 'classnames';
import styled, { css, keyframes } from 'styled-components';

// components
import Badge from './sub-components/icon-badge';

// constants
import materialIcons from './constants/material-icons-constants';

// style
import { sizes, variants } from './style';

export interface IconProps {
  name: keyof typeof materialIcons;
  className?: string;
  children?: React.ReactNode | string;
  animation?: 'default' | 'spin';
  outlined?: boolean;
  disabled?: boolean;
  position?: 'relative' | 'absolute';
  'data-name'?: string;
  'data-value'?: string | number;
  onMouseEnter?: () => void;
  'aria-label'?: string;
  role?: string;
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  alignSelf?: 'flex-start' | 'flex-end' | 'center';
  flip?: boolean;
  cursor?: 'pointer' | 'not-allowed';
  rotate?: number;
  clickable?: boolean;
  weight?: 200 | 300 | 500 | 'inherit';
  tabIndex?: number;
  style?: React.CSSProperties;
  display?: React.CSSProperties['display'];
  flexShrink?: React.CSSProperties['flexShrink'];
  title?: string;
}

export const IconComponent = ({
  name,
  className,
  children,
  animation,
  disabled,
  position,
  'data-name': dataName,
  'data-value': dataValue,
  onMouseEnter,
  role,
  'aria-label': ariaLabel,
  tabIndex,
  style,
  title,
}: IconProps): React.JSX.Element => {
  const icon = materialIcons[name]?.icon;

  return (
    <i
      tabIndex={tabIndex}
      style={style}
      className={classNames('box-icon', position, className, animation, 'material-symbols-outlined', { disabled })}
      role={role}
      data-name={dataName}
      aria-hidden={!ariaLabel}
      aria-label={ariaLabel}
      data-value={dataValue}
      onMouseEnter={onMouseEnter}
      title={title}
    >
      {icon}
      {children}
    </i>
  );
};

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const StyledIcon = styled(IconComponent)<IconProps>`
  &.box-icon {
    ${props => variants[props.variant || 'inherit']};
    font-size: ${props => sizes[props.size || 'lg']};
    max-width: ${props => sizes[props.size || 'lg']};
    display: flex;
    justify-content: center;
    transition: 0.2s;
    flex-shrink: ${props => props.flexShrink};
    ${props =>
      props.display &&
      css`
        display: ${props.display};
      `}

    &.disabled {
      cursor: not-allowed;
    }

    &.relative {
      position: relative;
    }

    &.absolute {
      position: absolute;
    }

    ${props =>
      props.alignSelf &&
      css`
        align-self: ${props.alignSelf};
      `};

    ${props =>
      props.flip &&
      css`
        transform: scaleX(-1);
      `};
    ${props =>
      props.rotate &&
      css`
        transform: rotate(${props.rotate}deg);
      `}
    ${props =>
      props.clickable &&
      css`
        &:not(.disabled) {
          cursor: pointer;
        }
      `}
		${props =>
      props.cursor &&
      css`
        cursor: ${props.cursor};
      `};
    ${props =>
      props.weight &&
      css`
        font-weight: ${props.weight};
      `};
    ${props =>
      props.animation === 'spin' &&
      css`
        animation: ${spin} 1s linear infinite;
      `};

    /* Material Symbols (the newer icons) support */
    &.material-symbols-outlined {
      font-variation-settings:
        'FILL' ${props => (props.outlined ? 0 : 1)},
        'wght' ${props => props.weight || 400};
    }
  }
`;

const Icon = (props: IconProps) => <StyledIcon {...props} />;

Icon.displayName = 'Icon';

Icon.Badge = Badge;

export default Icon;
